import { ReactNode, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface TooltipContextualProps {
  children?: ReactNode;
  content: ReactNode;
  className?: string;
  iconClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  showIcon?: boolean;
  triggerAsChild?: boolean;
}

/**
 * TooltipContextual - Tooltip reutilizable con icono de ayuda opcional
 * 
 * @example
 * // Con icono de ayuda (por defecto)
 * <TooltipContextual content="Esto es una explicación" />
 * 
 * // Envolviendo contenido existente
 * <TooltipContextual content="Explicación del botón" showIcon={false}>
 *   <Button>Mi Botón</Button>
 * </TooltipContextual>
 */
export function TooltipContextual({
  children,
  content,
  className,
  iconClassName,
  side = "top",
  align = "center",
  showIcon = true,
  triggerAsChild = false,
}: TooltipContextualProps) {
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild={triggerAsChild || !showIcon}>
          {showIcon && !children ? (
            <button
              type="button"
              className={cn(
                "inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20",
                iconClassName
              )}
              onClick={() => setOpen(!open)}
              aria-label="Más información"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          ) : (
            children
          )}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={cn("max-w-xs text-sm", className)}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
